import { format } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';

import JobStatusFilter from './JobStatusFilter';

import Avatar from '@/components/Avatar';
import Badge from '@/components/Badge';
import Button from '@/components/Button';
import Card from '@/components/Card';
import Dropdown from '@/components/Dropdown';
import LoadingState from '@/components/LoadingState';
import Pagination from '@/components/Pagination';
import Option from '@/components/Option';
import QueryEmptyState from '@/components/QueryEmptyState';
import Stack from '@/components/Stack';
import Table from '@/components/Table';
import TableHeader from '@/components/TableHeader';
import Tabs, { TabDefinition } from '@/components/Tabs';
import TextInput from '@/components/TextInput';
import { TIME_TO_REFRESH } from '@/constants/general';
import {
  JobWorkerStatusFilterEnum,
  JobWorkerStatusFilterEnumType,
} from '@/constants/job';
import Link from '@/elements/Link';
import { TableCell, TableRow } from '@/elements/Table';
import { useGetWorkerJobsQuery } from '@/graphql';
import useAuth from '@/hooks/useAuth';
import useFilters from '@/hooks/useFilters';
import ROUTES from '@/routes/routes';
import styled from '@/styles';
import {
  GetWorkerJobsQuery,
  GetWorkerQuery,
  JobWorkerFilterSetInput,
} from '@/types/graphql';
import {
  jobStatusResolver,
  jobWorkerStatusResolver,
  sortByDate,
} from '@/util/job';

const StyledStatusFilter = styled('div', {
  padding: 16,
});

const JOBS_TABS: TabDefinition[] = [
  { a11yLabel: 'View current shifts for this GravyWorker', label: 'Current' },
  { a11yLabel: 'View past shifts for this GravyWorker', label: 'Past' },
];

const initialFilters: JobWorkerFilterSetInput = {};
const ALL_SKILLS = 'ALL_SKILLS';
const NO_SHOW_STATUS = 'NO_SHOW';
const LATE_DROP_STATUS = 'LATE_DROP';

type JobItem = GetWorkerJobsQuery['worker']['jobWorkers']['items'][0]['job'] &
  Pick<GetWorkerJobsQuery['worker']['jobWorkers']['items'][0], 'showed'> & {
    jobWorkerStatus: JobWorkerStatusFilterEnumType;
  };

type Props = {
  workerId: GetWorkerQuery['worker']['id'] | number | string;
  currentAdminIsCustomerAdmin?: boolean;
  perPage?: number;
};

export const filterOptions = [
  { label: 'All', value: JobWorkerStatusFilterEnum.ALL },
  { label: 'In Progress', value: JobWorkerStatusFilterEnum.IN_PROGRESS },
  { label: 'Approved', value: JobWorkerStatusFilterEnum.APPROVED },
  { label: 'Completed', value: JobWorkerStatusFilterEnum.COMPLETED },
  { label: 'Dismissed', value: JobWorkerStatusFilterEnum.DISMISSED },
  { label: 'Dropped', value: JobWorkerStatusFilterEnum.DROPPED },
  { label: 'Expired', value: JobWorkerStatusFilterEnum.EXPIRED },
  { label: 'Hired', value: JobWorkerStatusFilterEnum.HIRED },
  { label: 'Paid', value: JobWorkerStatusFilterEnum.PAID },
  { label: 'No Show', value: NO_SHOW_STATUS },
  { label: 'Late Drop', value: LATE_DROP_STATUS },
];

const headerFields = [
  ['Skill', 'Client', 'Timeframe', 'Status', 'Shift Status'],
  ['Skill', 'Client', 'Timeframe', 'Status', 'Shift Status'],
];

const JobsList = ({
  workerId,
  currentAdminIsCustomerAdmin,
  perPage = 5,
}: Props) => {
  const [tab, setTab] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedStatus, setSelectedStatus] = useState<string>(
    JobWorkerStatusFilterEnum.ALL,
  );
  const [selectedSkill, setSelectedSkill] = useState(ALL_SKILLS);

  const {
    currentAdmin,
    currentAdminIsCustomerAdmin: authCurrentAdminIsCustomerAdmin,
    isCurrentCustomerAdmin,
  } = useAuth();
  const adminIsCustomerAdmin =
    currentAdminIsCustomerAdmin ?? authCurrentAdminIsCustomerAdmin;

  const {
    debouncedQuery,
    filters,
    query: searchQuery,
    clearFilter,
    setFilter,
    setQuery,
  } = useFilters<JobWorkerFilterSetInput>(initialFilters);

  const finalFilters = useMemo<JobWorkerFilterSetInput>(() => {
    const value = { ...filters };

    if (debouncedQuery !== '') {
      value.query = { value: debouncedQuery };
    }

    return value;
  }, [debouncedQuery, filters]);

  const query = useGetWorkerJobsQuery({
    variables: {
      filters: finalFilters,
      workerId: String(workerId),
      page,
      perPage,
    },
    pollInterval: TIME_TO_REFRESH,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const TableBody = useCallback(
    ({ jobs }: { jobs: JobItem[] }) => (
      <tbody>
        {jobs.map((item: JobItem) => {
          const permittedNavigate =
            adminIsCustomerAdmin &&
            !!currentAdmin &&
            isCurrentCustomerAdmin(currentAdmin) &&
            currentAdmin.customer.id === item.customer.id;
          const canNavigateToJob = !adminIsCustomerAdmin || permittedNavigate;
          const jobPath = canNavigateToJob
            ? `/orders/${item.order.id}/jobs/${item.id}`
            : null;
          const customerPath = !adminIsCustomerAdmin
            ? `/clients/${item.customer.id}`
            : permittedNavigate
              ? `/${ROUTES.myCompany}`
              : null;

          const jobStatusBadgeInfo = jobStatusResolver(item.status);
          const jobWorkerStatusBadgeInfo = jobWorkerStatusResolver(
            item.jobWorkerStatus,
          );

          return (
            <TableRow key={item.id}>
              <TableCell>
                <Stack gap={12}>
                  <Avatar size="xs" src={item.skill.imageUrl} />
                  {jobPath ? (
                    <Link to={jobPath}>{item.skill.name}</Link>
                  ) : (
                    <span>{item.skill.name}</span>
                  )}
                </Stack>
              </TableCell>
              <TableCell>
                <Stack gap={12}>
                  <div>
                    <Avatar
                      firstName={item.customer.name}
                      size="xs"
                      src={item.customer.logoUrl}
                    />
                  </div>
                  {customerPath ? (
                    <Link to={customerPath}>{item.customer.name}</Link>
                  ) : (
                    <span>{item.customer.name}</span>
                  )}
                </Stack>
              </TableCell>
              <TableCell>
                {format(new Date(item.firstShiftStartAt), 'PP')} &mdash;{' '}
                {format(new Date(item.lastShiftEndAt), 'PP')}
              </TableCell>
              <TableCell>
                {!item.showed ? (
                  <Badge label="No show" size="sm" status="warning" />
                ) : (
                  <Badge
                    label={jobWorkerStatusBadgeInfo.label}
                    status={jobWorkerStatusBadgeInfo.color}
                  />
                )}
              </TableCell>
              <TableCell align="center">
                <Badge
                  label={jobStatusBadgeInfo.label}
                  status={jobStatusBadgeInfo.color}
                />
              </TableCell>
            </TableRow>
          );
        })}
        {query.loading && (
          <TableRow>
            <TableCell align="center" colSpan={100}>
              <LoadingState overlayColor="white" />
            </TableCell>
          </TableRow>
        )}
      </tbody>
    ),
    [adminIsCustomerAdmin, currentAdmin, query],
  );

  const { jobWorkers } = query.data?.worker || {};
  const { items, pageInfo } = jobWorkers || {};

  const jobs = items
    ? items.map((item) => ({
        ...item.job,
        jobWorkerStatus: item.status,
        showed: item.showed,
      }))
    : [];

  const skillOptions = useMemo(() => {
    const skills = Array.from(new Set(jobs.map((job) => job.skill.name))).sort(
      (a, b) => a.localeCompare(b),
    );

    return [
      { label: 'All Skills', value: ALL_SKILLS },
      ...skills.map((skill) => ({ label: skill, value: skill })),
    ];
  }, [jobs]);

  const filteredJobs = useMemo(() => {
    const sortedJobs = sortByDate([...jobs]) as JobItem[];
    const statusFilteredJobs =
      selectedStatus === NO_SHOW_STATUS
        ? sortedJobs.filter((job) => !job.showed)
        : selectedStatus === LATE_DROP_STATUS
          ? sortedJobs.filter((job) => job.jobWorkerStatus === 'DROPPED')
          : sortedJobs;

    if (selectedSkill === ALL_SKILLS) {
      return statusFilteredJobs;
    }

    return statusFilteredJobs.filter((job) => job.skill.name === selectedSkill);
  }, [jobs, selectedSkill, selectedStatus]);

  const parseOption = useMemo(() => {
    const currentJobs = filterOptions.filter(
      (item) =>
        item.value === JobWorkerStatusFilterEnum.HIRED ||
        item.value === JobWorkerStatusFilterEnum.IN_PROGRESS ||
        item.value === JobWorkerStatusFilterEnum.ALL,
    );

    const pastJobs = filterOptions.filter(
      (item) =>
        item.value !== JobWorkerStatusFilterEnum.HIRED &&
        item.value !== JobWorkerStatusFilterEnum.IN_PROGRESS,
    );

    return tab === 0 ? currentJobs : pastJobs;
  }, [tab]);

  useEffect(() => {
    tab === 0
      ? setFilter('currentJobs', { value: true })
      : setFilter('currentJobs', { value: false });
  }, [tab, setFilter]);

  useEffect(() => {
    setPage(1);
  }, [tab, debouncedQuery, selectedSkill, selectedStatus]);

  const StatusDropdownContent = useCallback(
    () => (
      <StyledStatusFilter>
        <JobStatusFilter
          idPrefix="dropdown"
          jobStatusValue={selectedStatus}
          localOnlyValues={[NO_SHOW_STATUS, LATE_DROP_STATUS]}
          options={parseOption}
          setJobStatusValue={setSelectedStatus}
          onChangeFilter={setFilter}
          onClearFilter={clearFilter}
        />
      </StyledStatusFilter>
    ),
    [clearFilter, parseOption, selectedStatus, setFilter],
  );

  const SkillDropdownContent = useCallback(
    () => (
      <StyledStatusFilter>
        <Stack vertical gap={16}>
          {skillOptions.map((option, index) => (
            <Option
              key={`options-skill-${index}`}
              appearance="bullseye"
              checked={selectedSkill === option.value}
              id={`dropdown_skill-${index}`}
              label={option.label}
              name="dropdown_skill"
              type="radio"
              value={option.value}
              onChange={() => setSelectedSkill(option.value)}
            />
          ))}
          {selectedSkill !== ALL_SKILLS && (
            <Button
              a11yLabel="Clear skill filter"
              appearance="plain"
              label="Clear"
              onClick={() => setSelectedSkill(ALL_SKILLS)}
            />
          )}
        </Stack>
      </StyledStatusFilter>
    ),
    [selectedSkill, skillOptions],
  );

  return (
    <Card title="Shifts">
      <Tabs
        fit
        selected={tab}
        showTabOnMobile={false}
        tabs={JOBS_TABS}
        onSelect={setTab}
      />
      <Card.Section>
        <Stack gap={16}>
          <div style={{ flex: 1 }}>
            <TextInput
              id="input-search-jobs"
              placeholder="Search for shifts..."
              value={searchQuery}
              onChange={(ev) => {
                setQuery(ev.currentTarget.value);
              }}
            />
          </div>
          <Dropdown Content={StatusDropdownContent} justify="right" size="sm">
            <Button
              hasPopover
              a11yLabel="View shift worker status filters"
              appearance="outline"
              id="btn-job-status-filter"
              label="Status"
            />
          </Dropdown>
          <Dropdown Content={SkillDropdownContent} justify="right" size="sm">
            <Button
              hasPopover
              a11yLabel="View skill filters"
              appearance="outline"
              id="btn-job-skill-filter"
              label="Skill"
            />
          </Dropdown>
        </Stack>
      </Card.Section>
      <Card>
        <Table>
          <TableHeader fields={headerFields[tab]} />
          <TableBody jobs={filteredJobs} />
        </Table>
        {!query.loading && filteredJobs.length === 0 && (
          <QueryEmptyState
            query={query}
            text="If there were any, they would be here."
            title="No Shifts"
          />
        )}
        {jobs.length > 0 && pageInfo && pageInfo.totalPages > 1 && (
          <Card.Section>
            <Pagination
              currentPage={page}
              totalPages={pageInfo.totalPages}
              onPageChange={setPage}
            />
          </Card.Section>
        )}
      </Card>
    </Card>
  );
};

export default JobsList;
